'use client'

import { CopyIcon } from '@repo/ui/CopyIcon'
import { LinkIcon } from '@repo/ui/LinkIcon'
import { useGlobalSettingsStore } from '@repo/store/useGlobalSettingsStore'
import { useState } from 'react'
import MUIButton from '@mui/material/Button'
import MUIStack from '@mui/material/Stack'
import MUITooltip, { tooltipClasses } from '@mui/material/Tooltip'
import MUITypography from '@mui/material/Typography'
import PropTypes from 'prop-types'

export const ClientMatterDetailsMenuItem = ({
  title,
  label,
  icon,
  secondaryLabel,
  variant = 'medium',
  onClick,
  enableCopy = true,
  enableLink = true,
  children,
  noWrap = true,
  width = '100%',
  sx,
  showHoverIcon,
  WorktypeHover,
  hoverElement,
  isClientMatterDetailsPill = false
}) => {
  const [tooltipPrompt, setTooltipPrompt] = useState('Copy')
  const copyFunctionalityEnabled = enableCopy && label && label !== '-'
  const { isOutlookAddin } = useGlobalSettingsStore()

  let HoverIconComponent = () => null
  if (copyFunctionalityEnabled) HoverIconComponent = CopyIcon
  else if (onClick && enableLink) HoverIconComponent = LinkIcon

  const handleClick = (e, value) => {
    if (enableCopy) {
      setTooltipPrompt('Copied!')
      navigator.clipboard.writeText(value)
      setTimeout(() => setTooltipPrompt('Copy'), 2000)
    } else if (onClick) onClick(e)
  }

  let titleValue = ''
  if (copyFunctionalityEnabled) {
    titleValue = tooltipPrompt
  } else if (WorktypeHover) {
    titleValue = WorktypeHover
  } else if (hoverElement) {
    titleValue = hoverElement
  }

  return (
    <MUITooltip
      title={isOutlookAddin ? '' : titleValue}
      placement={hoverElement || WorktypeHover ? 'bottom' : 'top-end'}
      arrow
      enterDelay={20}
      PopperProps={{
        sx: {
          pointerEvents: 'none',
          [`& .${tooltipClasses.tooltipPlacementTop}`]: {
            transform: 'translate(0%, 50%) !important'
          },
          [`& .${tooltipClasses.arrow}`]: {
            right: '16px',
            left: WorktypeHover || hoverElement ? '0 !important' : 'auto !important'
          }
        }
      }}
    >
      <MUIButton
        onClick={(e) => handleClick(e, label)}
        sx={{
          width,
          height: 'auto',
          alignItems: 'flex-start',
          mb: 4,
          minWidth: '200px',
          textTransform: 'none',
          padding: '4px 12px',
          cursor: isClientMatterDetailsPill || (isOutlookAddin && !onClick) ? 'default' : 'pointer',
          '&:hover': {
            backgroundColor: isClientMatterDetailsPill || isOutlookAddin ? 'transparent' : 'inherit'
          },
          '&:hover .hoverIcon': { opacity: 1 },
          userSelect: isOutlookAddin && !onClick ? 'text' : 'none',
          pointerEvents:
            onClick ||
            copyFunctionalityEnabled ||
            WorktypeHover ||
            hoverElement ||
            isClientMatterDetailsPill ||
            isOutlookAddin
              ? 'auto'
              : 'none',
          ...sx
        }}
        data-testid="ClientMatterDetailsMenuItem"
      >
        <MUIStack
          direction="row"
          gap={4}
          alignItems="center"
          justifyContent="space-between"
          sx={{ width: '100%', minHeight: '30px', position: 'relative' }}
        >
          {icon || null}

          <MUIStack direction="column" sx={{ width: '100%' }}>
            {title ? (
              <MUITypography
                variant={variant === 'large' ? 'subtitle1' : 'subtitle2'}
                sx={{
                  color: showHoverIcon ? '#5073C3' : '#051C33',
                  padding: '0px',
                  opacity: 1,
                  mb: 1,
                  textDecoration: onClick ? 'underline' : 'none',
                  height: 'auto',
                  overflowY: 'hidden',
                  width: '100%',
                  textAlign: 'left',
                  cursor: onClick ? 'pointer' : 'auto',
                  pointerEvents: isOutlookAddin ? 'auto' : 'none',
                  textTransform: 'uppercase',
                  fontWeight: 500
                }}
                tabIndex="0"
                noWrap={noWrap}
              >
                {title}
              </MUITypography>
            ) : null}

            <MUITypography
              variant="body1"
              sx={{
                width: 'calc(100% - 16px)',
                textAlign: 'left',
                color: 'text.secondary',
                mb: 0,
                cursor: onClick ? 'pointer' : 'auto',
                pointerEvents: isOutlookAddin ? 'auto' : 'none',
                whiteSpace: noWrap ? 'nowrap' : 'pre-wrap'
              }}
              noWrap={noWrap}
            >
              {label}
            </MUITypography>

            {secondaryLabel ? (
              <MUITypography
                variant="body2"
                sx={{
                  width: '100%',
                  textAlign: 'left',
                  mb: 0,
                  color: 'text.primary',
                  cursor: onClick ? 'pointer' : 'auto',
                  pointerEvents: isOutlookAddin ? 'auto' : 'none'
                }}
                noWrap={noWrap}
              >
                {secondaryLabel}
              </MUITypography>
            ) : null}

            {children || null}
          </MUIStack>

          {!isOutlookAddin && (
            <HoverIconComponent
              sx={{
                fontSize: '16px',
                width: '32px',
                height: '32px',
                padding: '8px',
                opacity: showHoverIcon ? 1 : 0,
                color: '#3D517B',
                position: 'absolute',
                right: '0px',
                top: '50%',
                transform: 'translateY(-50%)',
                backgroundColor: 'inherit',
                borderRadius: '6px'
              }}
              className="hoverIcon"
            />
          )}
        </MUIStack>
      </MUIButton>
    </MUITooltip>
  )
}

ClientMatterDetailsMenuItem.propTypes = {
  title: PropTypes.string,
  label: PropTypes.any,
  icon: PropTypes.node,
  secondaryLabel: PropTypes.string,
  variant: PropTypes.string,
  onClick: PropTypes.func,
  enableCopy: PropTypes.bool,
  enableLink: PropTypes.bool,
  children: PropTypes.node,
  noWrap: PropTypes.bool,
  width: PropTypes.string,
  sx: PropTypes.object,
  showHoverIcon: PropTypes.bool,
  WorktypeHover: PropTypes.string,
  hoverElement: PropTypes.node,
  isClientMatterDetailsPill: PropTypes.bool
}
